import type { BuilderPage, PageSection } from './section-library';
import type { PageSettings } from './page-settings';
import { collectLinkIssues, type SitePageLink } from '@/lib/site/site-links';

export type SeoIssueSeverity = 'error' | 'warning';

export interface SeoIssue {
  id: string;
  severity: SeoIssueSeverity;
  message: string;
}

function hasMeaningfulImage(section: PageSection): boolean {
  const content = section.content ?? {};
  for (const [key, value] of Object.entries(content)) {
    if (typeof value === 'string' && /image|src|poster|og/i.test(key) && value.trim() !== '') {
      const altKey = key.replace(/image|src|url/i, 'alt').replace(/Url$/, 'Alt');
      const alt = content[altKey] ?? content.alt ?? content.imageAlt;
      if (typeof alt !== 'string' || alt.trim() === '') {
        return true;
      }
    }
  }
  return false;
}

/** Actionable SEO checks for a builder page. No fabricated scores. */
export function buildSeoChecklist(
  page: BuilderPage,
  pageSlug: string,
  sitePages: SitePageLink[] = [],
): SeoIssue[] {
  const settings: PageSettings = page.settings ?? {};
  const issues: SeoIssue[] = [];
  const title = (settings.title ?? '').trim();
  const description = (settings.description ?? '').trim();
  const slug = pageSlug.trim();

  if (!title) {
    issues.push({ id: 'missing-title', severity: 'error', message: 'Add an SEO title for this page.' });
  } else if (title.length > 60) {
    issues.push({
      id: 'long-title',
      severity: 'warning',
      message: `SEO title is ${title.length} characters. Many listings truncate around 60.`,
    });
  }

  if (!description) {
    issues.push({ id: 'missing-description', severity: 'error', message: 'Add a meta description.' });
  } else if (description.length > 160) {
    issues.push({
      id: 'long-description',
      severity: 'warning',
      message: `Meta description is ${description.length} characters. Many listings truncate around 160.`,
    });
  }

  if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    issues.push({ id: 'bad-slug', severity: 'error', message: 'Page slug must be lowercase letters, numbers, and hyphens.' });
  }

  if (settings.noIndex) {
    issues.push({
      id: 'noindex',
      severity: 'warning',
      message: 'This page is set to noindex and will stay out of search results when published.',
    });
  }

  if (!settings.ogImage && !settings.canonicalUrl) {
    issues.push({
      id: 'missing-social',
      severity: 'warning',
      message: 'Add an Open Graph image or a canonical URL for clearer sharing and indexing.',
    });
  }

  if (!settings.ogImage) {
    issues.push({
      id: 'missing-og-image',
      severity: 'warning',
      message: 'No social-sharing image is set.',
    });
  }

  const headingSections = page.sections.filter((section) => {
    const headline = section.content?.headline ?? section.content?.title;
    return typeof headline === 'string' && headline.trim() !== '';
  });
  if (headingSections.length === 0 && page.sections.length > 0) {
    issues.push({
      id: 'missing-headings',
      severity: 'warning',
      message: 'No section headlines were found. Add a clear heading near the top of the page.',
    });
  }

  if (page.sections.some(hasMeaningfulImage)) {
    issues.push({
      id: 'missing-alt',
      severity: 'warning',
      message: 'At least one image field may be missing alternative text. Check image settings.',
    });
  }

  const linkIssues = collectLinkIssues(page.sections, sitePages);
  for (const issue of linkIssues.slice(0, 8)) {
    issues.push({
      id: `link-${issue.href}`,
      severity: 'error',
      message:
        issue.kind === 'draft'
          ? `Link “${issue.label}” points to an unpublished page (${issue.href}).`
          : `Link “${issue.label}” points to a missing page (${issue.href}).`,
    });
  }

  return issues;
}
